import { tz } from "@date-fns/tz";
import {
  addDays,
  addHours,
  addMilliseconds,
  addMinutes,
  addMonths,
  addYears,
  endOfDay,
  endOfMonth,
  endOfYear,
  format,
  startOfDay,
  startOfMonth,
  startOfYear,
} from "date-fns";

import {
  ChartDateType,
  EnergyType,
  GetRecordsResponse,
  HelperFunctionsChartRecords,
  LiveData,
  PowerPlant,
  PreparedRecord,
  ChartGranularityType,
} from "api";
// Deep import (not the "api" barrel): the barrel pulls in axios/window via
// setup.ts, which breaks this module's unit tests in a node env.
import { energyTypeList } from "api/queries/records/types";
import { formatEnergy, formatPower } from "utils/energy";

const MS_PER_HOUR = 60 * 60 * 1000;

/** Record with the latest `date` (rows aren't guaranteed sorted here). */
export const latestRecord = <T extends { date: string }>(rows: T[]): T =>
  rows.reduce((latest, r) =>
    new Date(r.date) > new Date(latest.date) ? r : latest,
  );

/** Cumulative value at the END of a record's window (raw + delta). */
/**
 * The device's own reading out of a live metric, with a fallback to `total`
 * for a payload from before the two were told apart. Same rule as
 * `@solar/core`'s `counterOf`; the two apps share the bug, so they share the
 * fix.
 */
export const counterOf = (metric: { total: number; counter?: number }): number =>
  metric.counter ?? metric.total;

export const cumulativeAtEnd = (metric: {
  raw: number;
  calculated: number;
}): number => metric.raw + metric.calculated;

export const createHelperFunctionsGrouping = <
  SelectedMetrics extends EnergyType =
    | "energyConsumed"
    | "energyProduced"
    | "inductiveEnergyConsumed"
    | "capacitiveEnergyProduced"
    | "inductiveEnergyProduced"
    | "capacitiveEnergyConsumed",
>(
  powerPlant?: PowerPlant,
  chartGranularityType?: ChartGranularityType,
): HelperFunctionsChartRecords<SelectedMetrics> => {
  const timezone = tz(powerPlant?.timezone || "Europe/Warsaw");
  const startDate = powerPlant?.startDate
    ? new Date(powerPlant.startDate)
    : new Date();

  const helpers: HelperFunctionsChartRecords<SelectedMetrics> = {
    day: {
      start: (date: Date) =>
        startOfDay(date, {
          in: timezone,
        }),
      end: (date: Date) =>
        endOfDay(date, {
          in: timezone,
        }),
      formatXAxis: (date: Date) => {
        const dateString = format(date, "HH:mm", { in: timezone });
        return dateString === "00:00" || dateString === "23:55"
          ? ""
          : dateString;
      },
      formatXTooltip: (date: Date, period?: number) =>
        period
          ? `${format(date, "HH:mm", { in: timezone })} - ${format(addMilliseconds(date, period), "HH:mm", { in: timezone })}`
          : format(date, "HH:mm", { in: timezone }),
      formatYAxis: (value: number) =>
        chartGranularityType === "linear"
          ? formatPower(value).formatted
          : formatEnergy(value).formatted,
      formatYTooltip: (value: number) =>
        chartGranularityType === "linear"
          ? formatPower(value).formatted
          : formatEnergy(value).formatted,
      formatDateType: (date: Date) => format(date, "P", { in: timezone }),
      chartOptions: {
        scaleType: "time", // should be auto, but there's some issue with generated ticks
        seriesType: chartGranularityType === "linear" ? "line" : "bar",
        isPower: chartGranularityType === "linear",
      },
      prepareData: (data: GetRecordsResponse<SelectedMetrics>, date: Date, effectiveNow?: Date) => {
        const intervalMinutes =
          chartGranularityType === "15minutes"
            ? 15
            : chartGranularityType === "1hour"
              ? 60
              : 5;

        const isLinear = chartGranularityType === "linear";

        return generateEmptyValuesIfMissing(
          prepareData(
            data,
            isLinear,
            intervalMinutes > 5 ? intervalMinutes : undefined,
            effectiveNow,
          ),
          helpers.day.start(date),
          helpers.day.end(date),
          (date: Date) => addMinutes(date, intervalMinutes),
        );
      },
      generateTicks: (date) =>
        generateTicks(
          helpers.day.start(date),
          helpers.day.end(date),
          (date: Date) => addHours(date, 3),
        ),
    },
    month: {
      start: (date: Date) =>
        startOfMonth(date, {
          in: timezone,
        }),
      end: (date: Date) =>
        endOfMonth(date, {
          in: timezone,
        }),
      formatXAxis: (date: Date) => format(date, "d", { in: timezone }),
      formatXTooltip: (date: Date) => format(date, "PPP", { in: timezone }),
      formatYAxis: (value: number) => formatEnergy(value).formatted,
      formatYTooltip: (value: number) => formatEnergy(value).formatted,
      formatDateType: (date: Date) => format(date, "M.y", { in: timezone }),
      chartOptions: {
        scaleType: "auto",
        seriesType: "bar",
        isPower: false,
      },
      prepareData: (data: GetRecordsResponse<SelectedMetrics>, date: Date) =>
        generateEmptyValuesIfMissing(
          prepareData(data),
          helpers.month.start(date),
          helpers.month.end(date),
          (date: Date) => addDays(date, 1),
        ),
      generateTicks: (date) =>
        generateTicks(
          helpers.month.start(date),
          helpers.month.end(date),
          (date: Date) => addDays(date, 1),
        ),
    },
    year: {
      start: (date: Date) =>
        startOfYear(date, {
          in: timezone,
        }),
      end: (date: Date) =>
        endOfYear(date, {
          in: timezone,
        }),
      formatXAxis: (date: Date) => format(date, "M", { in: timezone }),
      formatXTooltip: (date: Date) => format(date, "LLLL Y", { in: timezone }),
      formatYAxis: (value: number) => formatEnergy(value).formatted,
      formatYTooltip: (value: number) => formatEnergy(value).formatted,
      formatDateType: (date: Date) => format(date, "y", { in: timezone }),
      chartOptions: {
        scaleType: "auto",
        seriesType: "bar",
        isPower: false,
      },
      prepareData: (data: GetRecordsResponse<SelectedMetrics>, date: Date) =>
        generateEmptyValuesIfMissing(
          prepareData(data),
          helpers.year.start(date),
          helpers.year.end(date),
          (date: Date) => addMonths(date, 1),
        ),
      generateTicks: (date) =>
        generateTicks(
          helpers.year.start(date),
          helpers.year.end(date),
          (date: Date) => addMonths(date, 1),
        ),
    },
    total: {
      start: () =>
        startOfYear(startDate, {
          in: timezone,
        }),
      end: () =>
        startOfYear(addYears(new Date(), 1), {
          in: timezone,
        }),
      formatXAxis: (date: Date) => format(date, "y", { in: timezone }),
      formatXTooltip: (date: Date) => format(date, "y", { in: timezone }),
      formatYAxis: (value: number) => formatEnergy(value).formatted,
      formatYTooltip: (value: number) => formatEnergy(value).formatted,
      chartOptions: {
        scaleType: "auto",
        seriesType: "bar",
        isPower: false,
      },
      prepareData: (data: GetRecordsResponse<SelectedMetrics>) =>
        prepareData(data),
      generateTicks: (date) =>
        generateTicks(
          helpers.total.start(date),
          helpers.total.end(date),
          (date: Date) => addYears(date, 1),
        ),
    },
  };
  return helpers;
};

export const prepareData = <
  SelectedMetrics extends EnergyType =
    | "energyConsumed"
    | "energyProduced"
    | "inductiveEnergyConsumed"
    | "capacitiveEnergyProduced"
    | "inductiveEnergyProduced"
    | "capacitiveEnergyConsumed",
>(
  data: GetRecordsResponse<SelectedMetrics>,
  calculatePower = false,
  groupByMinutes?: number,
  effectiveNow?: Date,
): PreparedRecord<SelectedMetrics>[] => {
  const sortedData = data.sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );

  const processedData = sortedData.map((record) => {
    const updatedMetrics = Object.keys(record).reduce(
      (acc, field) => {
        if (field === "date" || field === "period") return acc;
        const currentMetric = record[field as SelectedMetrics];

        if (
          calculatePower &&
          typeof currentMetric === "object" &&
          record.period
        )
          acc[field as SelectedMetrics] =
            (currentMetric.calculated / record.period) * MS_PER_HOUR;
        else if (typeof currentMetric === "object")
          acc[field as SelectedMetrics] = currentMetric.calculated;
        else acc[field as SelectedMetrics] = 0;

        return acc;
      },
      {} as {
        [K in SelectedMetrics]: number;
      },
    );

    return {
      ...record,
      ...updatedMetrics,
      date: new Date(record.date),
    };
  }) as PreparedRecord<SelectedMetrics>[];

  // If grouping is requested, group the data by intervals
  if (groupByMinutes) {
    return groupByInterval(processedData, groupByMinutes, calculatePower, effectiveNow);
  }

  return processedData;
};

const generateEmptyValuesIfMissing = <
  SelectedMetrics extends EnergyType =
    | "energyConsumed"
    | "energyProduced"
    | "inductiveEnergyConsumed"
    | "capacitiveEnergyProduced"
    | "inductiveEnergyProduced"
    | "capacitiveEnergyConsumed",
>(
  data: PreparedRecord<SelectedMetrics>[],
  start: Date,
  end: Date,
  intervalFunction: (date: Date) => Date,
): PreparedRecord<SelectedMetrics>[] => {
  const metrics = Object.keys(data[0] || {}).filter(
    (key) => key !== "date" && key !== "period",
  ) as SelectedMetrics[];

  let current = start;

  while (current <= end) {
    const nextDate = intervalFunction(current);
    const existingRecord = data.some(
      (record) => record.date >= current && record.date < nextDate,
    );

    if (!existingRecord) {
      const emptyRecord = metrics.reduce(
        (acc, metric) => {
          acc[metric] = undefined;
          return acc;
        },
        {} as { [K in SelectedMetrics]: number | undefined },
      );

      data.push({
        ...emptyRecord,
        date: current,
        period: nextDate.getTime() - current.getTime(),
      });
    }

    current = nextDate;
  }

  return data.sort((a, b) => a.date.getTime() - b.date.getTime());
};

export const groupByInterval = <
  SelectedMetrics extends EnergyType =
    | "energyConsumed"
    | "energyProduced"
    | "inductiveEnergyConsumed"
    | "capacitiveEnergyProduced"
    | "inductiveEnergyProduced"
    | "capacitiveEnergyConsumed",
>(
  data: PreparedRecord<SelectedMetrics>[],
  intervalMinutes: number,
  isPowerData: boolean = false,
  effectiveNow?: Date,
): PreparedRecord<SelectedMetrics>[] => {
  if (data.length === 0) return [];

  const metrics = Object.keys(data[0]).filter(
    (key) => key !== "date" && key !== "period",
  ) as SelectedMetrics[];

  const grouped: { [key: string]: PreparedRecord<SelectedMetrics>[] } = {};

  data.forEach((record) => {
    const intervalStart = new Date(record.date);
    const minutes = intervalStart.getMinutes();
    const roundedMinutes =
      Math.floor(minutes / intervalMinutes) * intervalMinutes;
    intervalStart.setMinutes(roundedMinutes, 0, 0);

    const key = intervalStart.getTime().toString();

    if (!grouped[key]) {
      grouped[key] = [];
    }
    grouped[key].push(record);
  });

  // Determine effective "now": take the later of the last record's end time
  // and the live data timestamp (if provided). Live data is more frequent
  // than energy records so gives a more accurate elapsed time for the
  // current interval.
  const effectiveNowMs = Math.max(
    data.reduce((max, r) => {
      return Math.max(max, r.date.getTime() + (r.period || 5 * 60 * 1000));
    }, 0),
    effectiveNow?.getTime() ?? 0,
  );

  return Object.entries(grouped)
    .map(([timestamp, records]) => {
      const intervalStart = new Date(parseInt(timestamp));
      const intervalMs = intervalMinutes * 60 * 1000;
      const intervalEndMs = intervalStart.getTime() + intervalMs;

      // For the currently active interval use data-driven elapsed time.
      // For completed intervals fall back to sum of record periods.
      const actualElapsedMs =
        effectiveNowMs > intervalStart.getTime() && effectiveNowMs < intervalEndMs
          ? effectiveNowMs - intervalStart.getTime()
          : Math.min(
              records.reduce((sum, r) => sum + (r.period || 5 * 60 * 1000), 0),
              intervalMs,
            );

      const aggregatedRecord = {
        date: intervalStart,
        period: actualElapsedMs,
      } as PreparedRecord<SelectedMetrics>;

      metrics.forEach((metric) => {
        const validRecords = records.filter(
          (r) => r[metric] !== undefined && r[metric] !== null,
        );

        if (validRecords.length > 0) {
          if (isPowerData) {
            // Sum energy from each sub-record: power (W) × period (ms) → Wh,
            // then convert back to average power over the actual elapsed time.
            const totalEnergy = validRecords.reduce((sum, r) => {
              const power = r[metric] as number;
              const periodMs = r.period || 5 * 60 * 1000;
              return sum + (power * periodMs) / MS_PER_HOUR;
            }, 0);
            const actualElapsedHours = actualElapsedMs / MS_PER_HOUR;
            aggregatedRecord[metric] = (totalEnergy /
              actualElapsedHours) as PreparedRecord<SelectedMetrics>[SelectedMetrics];
          } else {
            const values = validRecords.map((r) => r[metric] as number);
            aggregatedRecord[metric] = values.reduce(
              (sum, val) => sum + val,
              0,
            ) as PreparedRecord<SelectedMetrics>[SelectedMetrics];
          }
        } else {
          aggregatedRecord[metric] =
            undefined as PreparedRecord<SelectedMetrics>[SelectedMetrics];
        }
      });

      return aggregatedRecord;
    })
    .sort((a, b) => a.date.getTime() - b.date.getTime());
};

export const generateTicks = (
  start: Date,
  end: Date,
  intervalFunction: (date: Date) => Date,
): number[] => {
  const ticks = [];
  let current = start;

  while (current <= end) {
    ticks.push(current.getTime());
    current = intervalFunction(current);
  }
  return ticks;
};

export type AppendLiveRecordParams = {
  liveData: LiveData | null | undefined;
  liveDataDate: Date | undefined;
  /** End-of-window timestamp of the latest stored record (ms). */
  lastRecordEndMs: number;
  isTodaySelected: boolean;
  dateType: ChartDateType;
  chartGranularityType?: ChartGranularityType;
};

/**
 * Append a synthetic record covering the gap between the last stored record
 * and the live-data timestamp, so partially-complete buckets reflect the
 * latest reading.
 *
 * The synthetic point is intentionally NOT added for an inverter-sourced
 * day + linear chart: that chart plots instantaneous power, and the inverter
 * live reading is imprecise enough to draw a misleading spike for the still-
 * open period — so we wait for the period to close (a real stored record).
 * Meter-sourced day + linear keeps the live point (the meter is precise),
 * and the 15min / 1hour bar buckets keep it for any source (they average
 * the value over the interval).
 *
 * Returns `data` untouched whenever a synthetic record shouldn't be added
 * (not "today", not the day view, inverter-sourced linear granularity, no
 * live data, or the live timestamp isn't ahead of the last record).
 */
export const appendLiveRecord = <SelectedMetrics extends EnergyType>(
  data: GetRecordsResponse<SelectedMetrics> | undefined,
  {
    liveData,
    liveDataDate,
    lastRecordEndMs,
    isTodaySelected,
    dateType,
    chartGranularityType,
  }: AppendLiveRecordParams,
): GetRecordsResponse<SelectedMetrics> | undefined => {
  const isInverterLinearDay =
    dateType === "day" &&
    chartGranularityType === "linear" &&
    liveData?.source === "inverter";
  if (
    !data ||
    !data.length ||
    !liveDataDate ||
    !liveData ||
    !isTodaySelected ||
    dateType !== "day" ||
    isInverterLinearDay
  ) {
    return data;
  }

  const lastRecord = latestRecord(data);

  // Synthetic record starts at the END of the last record, not its start.
  const periodMs = liveDataDate.getTime() - lastRecordEndMs;
  // Nothing to add if live data is within or before the last record's window.
  if (periodMs <= 0) return data;

  const syntheticRecord = {
    date: new Date(lastRecordEndMs).toISOString(),
    period: periodMs,
    // Inverter-sourced snapshots don't include meter-only metrics; skip
    // those entirely rather than emitting fabricated values that would draw
    // a flat-zero (or worse, stale) line until the next real record arrives.
    ...Object.fromEntries(
      energyTypeList.flatMap((metric) => {
        const live = liveData[metric];
        if (!live) return [];
        const last = lastRecord[metric as SelectedMetrics];
        return [
          [
            metric,
            {
              // Energy since the end of the last record (don't double-count).
              // **`counter`, not `total`**: `total` is the plant's lifetime
              // energy across every source, so subtracting a record's own
              // counter from it gives a nonsense figure — negative here, which
              // the clamp turned into a flat zero for the live period.
              calculated: Math.max(0, counterOf(live) - cumulativeAtEnd(last)),
              raw: counterOf(live),
            },
          ],
        ];
      }),
    ),
  } as GetRecordsResponse<SelectedMetrics>[number];

  return [...data, syntheticRecord];
};

export type InverterTipState = {
  /** Cumulative energyProduced at the last accepted tick. */
  lastTotal?: number;
  /** Held running-average power (kW) shown on the inverter linear tip. */
  tipPower?: number;
};

/** Stable empty state so callers can identity-compare to skip re-renders. */
export const EMPTY_INVERTER_TIP: InverterTipState = {};

/**
 * Tick-gated running average for the inverter day+linear live tip.
 *
 * The inverter's cumulative energy counter is ~0.1 kWh quantized, so
 * recomputing power = Δenergy/elapsed on every ~15s refresh makes the open
 * point sag toward 0 then spike when the counter ticks. This advances the
 * tip ONLY when `liveTotal` changes (a real counter tick), sampling the
 * average from the open interval's start up to that tick — accurate, since
 * both energy and elapsed are read at the tick. Between ticks, and across an
 * interval rollover until the next interval's first tick, it returns `prev`
 * unchanged (identity-equal) so the caller holds the last value.
 */
export const nextInverterTip = (
  prev: InverterTipState,
  args: {
    liveTotal: number | undefined;
    openIntervalBaseTotal: number | undefined;
    liveDataMs: number | undefined;
    lastRecordEndMs: number;
  },
): InverterTipState => {
  const { liveTotal, openIntervalBaseTotal, liveDataMs, lastRecordEndMs } =
    args;
  if (
    liveTotal === undefined ||
    openIntervalBaseTotal === undefined ||
    !liveDataMs ||
    !lastRecordEndMs
  ) {
    return prev; // not enough info → hold
  }
  if (liveTotal === prev.lastTotal) return prev; // no tick → hold

  const elapsedHours = (liveDataMs - lastRecordEndMs) / MS_PER_HOUR;
  if (elapsedHours <= 0) {
    // Tick landed but the live timestamp isn't inside the open interval yet
    // (just after a rollover): consume the tick but hold the previous tip.
    return { lastTotal: liveTotal, tipPower: prev.tipPower };
  }
  return {
    lastTotal: liveTotal,
    tipPower: Math.max(0, (liveTotal - openIntervalBaseTotal) / elapsedHours),
  };
};

/**
 * Append the inverter linear chart's tick-gated live tip (from
 * {@link nextInverterTip}) as a record anchored at the open interval's
 * start, shaped so prepareData's `calculated/period*MS_PER_HOUR` resolves
 * back to exactly `tipPower`. Sibling of {@link appendLiveRecord}; the
 * caller picks which based on source/granularity. Returns `data` untouched
 * when there's nothing to add.
 */
export const appendInverterTipRecord = <SelectedMetrics extends EnergyType>(
  data: GetRecordsResponse<SelectedMetrics> | undefined,
  {
    tipPower,
    lastRecordEndMs,
    periodMs,
    liveTotal,
    isTodaySelected,
  }: {
    tipPower: number | undefined;
    lastRecordEndMs: number;
    periodMs: number;
    liveTotal: number | undefined;
    isTodaySelected: boolean;
  },
): GetRecordsResponse<SelectedMetrics> | undefined => {
  if (
    !data ||
    !data.length ||
    !isTodaySelected ||
    tipPower === undefined ||
    !lastRecordEndMs ||
    periodMs <= 0
  ) {
    return data;
  }
  const tipRecord = {
    date: new Date(lastRecordEndMs).toISOString(),
    period: periodMs,
    energyProduced: {
      calculated: (tipPower * periodMs) / MS_PER_HOUR,
      raw: liveTotal ?? 0,
    },
  } as GetRecordsResponse<SelectedMetrics>[number];
  return [...data, tipRecord];
};
